import { colors, intl, ui } from '@beelday/common'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { FC } from 'react'
import { LobbyEyebrow } from 'room/lobby/presentation/lobby-hero'
import { LobbySize } from 'room/lobby/presentation/lobby-size'
import { LobbyStatus } from 'room/lobby/presentation/lobby-status'
import groupAtAFlipChart from '../images/group-at-a-flip-chart.png'
import groupAtATable from '../images/group-at-a-table.png'

/** The stage's sizes; the phone has a screen of its own (`GroupWorkView`). */
export type GroupWorkCardSize = Exclude<LobbySize, 'phone'>

/**
 * A laptop's card grows with the window: a trainer presenting on one screen
 * shows this very window on the TV, which is read from the back of the room.
 */
const scale = {
	desktop: {
		card: css`
			max-width: 1240px;
			padding: 32px;
			gap: 24px;

			${ui.responsive.desktopFHD} {
				max-width: 1640px;
				padding: 48px;
			}
		`,
		heading: css`
			font-size: 28px;

			${ui.responsive.desktop1440} {
				font-size: 36px;
			}

			${ui.responsive.desktopFHD} {
				font-size: 48px;
			}
		`,
		text: css`
			font-size: 16px;

			${ui.responsive.desktop1440} {
				font-size: 20px;
			}

			${ui.responsive.desktopFHD} {
				font-size: 26px;
			}
		`,
		picture: css`
			max-height: 200px;

			${ui.responsive.desktop} {
				max-height: 360px;
			}

			${ui.responsive.desktopFHD} {
				max-height: 480px;
			}
		`,
	},
	room: {
		card: css`
			max-width: 1640px;
			padding: 56px 48px;
			gap: 32px;
		`,
		heading: css`
			font-size: 56px;
		`,
		text: css`
			font-size: 28px;
		`,
		picture: css`
			max-height: 220px;

			${ui.responsive.desktop} {
				max-height: 540px;
			}
		`,
	},
}

/**
 * The task between two pictures of people at work from 1280px up; on
 * anything narrower the pictures sit side by side above it.
 */
const Card = styled.section<{ $size: GroupWorkCardSize }>`
	box-sizing: border-box;
	width: 100%;
	margin: auto;
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	grid-template-areas:
		'left right'
		'task task';
	align-items: center;
	background: ${colors.white};
	border-radius: 16px;
	box-shadow: 0 12px 40px ${colors.darkIndigo15};
	${props => scale[props.$size].card}

	${ui.responsive.desktop} {
		grid-template-columns: minmax(0, 1fr) minmax(0, 1.55fr) minmax(0, 1fr);
		grid-template-areas: 'left task right';
	}
`

const Picture = styled.img<{ $size: GroupWorkCardSize; $area: string }>`
	grid-area: ${props => props.$area};
	justify-self: center;
	display: block;
	width: 100%;
	height: auto;
	object-fit: contain;
	user-select: none;
	${props => scale[props.$size].picture}
`

const Task = styled.div`
	grid-area: task;
	min-width: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 20px;
	text-align: center;
`

const Heading = styled.h1<{ $size: GroupWorkCardSize }>`
	margin: 0;
	color: ${colors.indigoBlue};
	font-weight: 700;
	line-height: 1.15;
	overflow-wrap: anywhere;
	${props => scale[props.$size].heading}
`

/**
 * `colors.blackish`, the body-text colour: read from the back of a room, a
 * lighter grey would wash out.
 */
const Description = styled.p<{ $size: GroupWorkCardSize }>`
	margin: 0;
	max-width: 40ch;
	color: ${colors.blackish};
	line-height: 1.5;
	white-space: pre-wrap;
	overflow-wrap: anywhere;
	${props => scale[props.$size].text}
`

const Meta = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: center;
	gap: 12px 20px;
	color: ${colors.brightBlackish};
	font-variant-numeric: tabular-nums;

	/* A status that wraps reads from its dot, not from the card's centre. */
	& [role='status'] {
		text-align: left;
	}
`

const BackAt = styled.span<{ $size: GroupWorkCardSize }>`
	font-size: ${props => (props.$size === 'room' ? 22 : 16)}px;
`

const HEADING_ID = 'group-work-heading'

type Props = {
	size: GroupWorkCardSize
	/** The task's name; the room is told the task comes in person without one. */
	name?: string
	description?: string
	/** The planned end, already formatted (`useGroupWorkEnd`). */
	backAt?: string
}

/**
 * The group-work screen of the stage, the projector and a participant's
 * laptop: the task - what the room keeps looking back at - between two
 * pictures of people at work, in the card the lobby uses for the break, and
 * the same line under it: the screen changes by itself.
 */
export const GroupWorkCard: FC<Props> = ({
	size,
	name,
	description,
	backAt,
}) => {
	// A task with only a description has no heading of its own; one with
	// neither is explained in the room, and the card says so.
	const heading =
		name ||
		(description ? null : (
			<intl.Translate>in_person.mobile.group_work.no_task</intl.Translate>
		))
	return (
		<Card
			$size={size}
			aria-labelledby={heading ? HEADING_ID : undefined}
			data-testid="simple_grouping.card"
		>
			<Picture
				$size={size}
				$area="left"
				src={groupAtATable}
				alt=""
				aria-hidden="true"
				draggable={false}
			/>
			<Task>
				<LobbyEyebrow $size={size}>
					<intl.Translate>features.simple-grouping.title</intl.Translate>
				</LobbyEyebrow>
				{heading ? (
					<Heading $size={size} id={HEADING_ID}>
						{heading}
					</Heading>
				) : null}
				{description ? (
					<Description $size={size}>{description}</Description>
				) : null}
				<Meta>
					<LobbyStatus size={size}>
						<intl.Translate>features.simple-grouping.status</intl.Translate>
					</LobbyStatus>
					{backAt ? (
						<BackAt $size={size} data-testid="simple_grouping.back_at">
							<intl.Translate time={backAt}>
								in_person.lobby.back_at
							</intl.Translate>
						</BackAt>
					) : null}
				</Meta>
			</Task>
			<Picture
				$size={size}
				$area="right"
				src={groupAtAFlipChart}
				alt=""
				aria-hidden="true"
				draggable={false}
			/>
		</Card>
	)
}
